/** Ollama 集成配置；复用集成弹窗，Access Secret 不回显且留空保留。 */
import {
  type OllamaCredentialResponse,
  UpsertOllamaCredentialRequestSchema,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useOllamaCredential } from '~/data/queries';
import { ollamaApi } from '~/hooks/use-ollama';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { PasswordInput } from '~/ui/PasswordInput';
import { toast } from '~/ui/Toast';
import { ProviderConcurrencyField } from './ProviderConcurrencyField';

function ConfigForm({ initial }: { initial: OllamaCredentialResponse }) {
  const [baseUrl, setBaseUrl] = useState(initial.base_url ?? '');
  const [clientId, setClientId] = useState(initial.access_client_id ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [timeoutSeconds, setTimeoutSeconds] = useState(String(initial.timeout_seconds ?? 60));
  const [error, setError] = useState('');
  const qc = useQueryClient();
  // Secret 只作为瞬时请求参数；mutationFn 不接收变量，避免进入 mutation cache。
  const save = useMutation({
    mutationFn: async () => {
      const input = UpsertOllamaCredentialRequestSchema.parse({
        base_url: baseUrl.trim(),
        access_client_id: clientId.trim(),
        ...(clientSecret.trim() ? { access_client_secret: clientSecret.trim() } : {}),
        timeout_seconds: Number(timeoutSeconds),
      });
      return ollamaApi.upsertCredential(input);
    },
    onSuccess: async () => {
      setClientSecret('');
      await qc.invalidateQueries({ queryKey: ['integrations', 'ollama'] });
      toast('Ollama 配置已保存', { tone: 'pos' });
    },
    onError: (e) => setError(e.message),
  });
  const submit = () => {
    const result = UpsertOllamaCredentialRequestSchema.safeParse({
      base_url: baseUrl.trim(),
      access_client_id: clientId.trim(),
      ...(clientSecret.trim() ? { access_client_secret: clientSecret.trim() } : {}),
      timeout_seconds: Number(timeoutSeconds),
    });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? '配置错误');
      return;
    }
    setError('');
    save.mutate();
  };
  return (
    <>
      <FormRow label="Base URL" required hint="Cloudflare Tunnel 的 HTTPS 地址，以 /v1 结尾">
        <input
          aria-label="Ollama Base URL"
          className="input mono"
          value={baseUrl}
          placeholder="https://ollama.example.com/v1"
          disabled={save.isPending}
          onChange={(e) => setBaseUrl(e.target.value)}
        />
      </FormRow>
      <FormRow label="Access Client ID" required hint="Cloudflare Access 服务令牌的 Client ID">
        <input
          aria-label="Ollama Access Client ID"
          className="input mono"
          value={clientId}
          disabled={save.isPending}
          onChange={(e) => setClientId(e.target.value)}
          autoComplete="off"
        />
      </FormRow>
      <FormRow
        label="Access Client Secret"
        required={!initial.configured}
        hint={
          initial.configured
            ? '地址和 Client ID 不变时可留空保留；修改任一项须重新填写 Secret'
            : '首次配置必须填写服务令牌 Secret'
        }
      >
        <PasswordInput
          aria-label="Ollama Access Client Secret"
          className="input mono"
          value={clientSecret}
          disabled={save.isPending}
          onChange={(e) => setClientSecret(e.target.value)}
          autoComplete="new-password"
        />
      </FormRow>
      <FormRow label="请求超时（秒）" required hint="30–120 秒；按本机模型处理速度配置">
        <input
          aria-label="Ollama 请求超时"
          className="input mono"
          type="number"
          min={30}
          max={120}
          step={1}
          value={timeoutSeconds}
          disabled={save.isPending}
          onChange={(e) => setTimeoutSeconds(e.target.value)}
        />
      </FormRow>
      <p className="muted">
        Secret
        加密存储，明文不会回显。保存和测试连接读取模型目录。模型与提示词在「大模型配置」中分别设置。
      </p>
      <p className="muted">
        本机、Ollama 与 Tunnel 必须保持运行；电脑休眠或断网时无法执行审核与脱敏。
      </p>
      {error && (
        <div role="alert" style={{ color: 'var(--neg)' }}>
          {error}
        </div>
      )}
      <button
        className="btn btn-accent"
        disabled={save.isPending || (!initial.configured && clientSecret.trim().length < 16)}
        onClick={submit}
      >
        {save.isPending ? '保存中…' : '保存配置'}
      </button>
    </>
  );
}

export function OllamaConfigDialog({ onClose }: { onClose: () => void }) {
  const q = useOllamaCredential();
  const qc = useQueryClient();
  const [removing, setRemoving] = useState(false);
  const test = useMutation({
    mutationFn: ollamaApi.testConnection,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['integrations', 'ollama'] });
      toast('Ollama 连接验证通过', { tone: 'pos' });
    },
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  const remove = useMutation({
    mutationFn: ollamaApi.removeCredential,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['integrations', 'ollama'] });
      setRemoving(false);
      toast('Ollama 配置已移除', { tone: 'warn' });
    },
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  return (
    <Dialog
      title="Ollama 集成配置"
      subtitle="本地模型 · Cloudflare Tunnel 与 Access 服务令牌"
      icon="IconBranch"
      width={620}
      onClose={onClose}
      closeOnEsc={!removing}
      footer={
        <button className="btn" onClick={onClose}>
          完成
        </button>
      }
    >
      <div style={{ padding: 18 }}>
        {q.isPending ? (
          <p>加载配置…</p>
        ) : q.isError ? (
          <div role="alert">{q.error.message}</div>
        ) : (
          <>
            <p>
              {q.data.configured ? `已配置 · Secret 尾号 ${q.data.secret_tail}` : '尚未配置 Ollama'}
            </p>
            <ConfigForm key={q.data.updated_at ?? 'unset'} initial={q.data} />
            {q.data.configured && (
              <div className="hstack" style={{ gap: 8, marginTop: 16 }}>
                <button
                  className="btn"
                  disabled={test.isPending || remove.isPending}
                  onClick={() => test.mutate()}
                >
                  {test.isPending ? '测试中…' : '测试连接'}
                </button>
                <button
                  className="btn btn-danger"
                  disabled={remove.isPending || test.isPending}
                  onClick={() => setRemoving(true)}
                >
                  移除配置
                </button>
              </div>
            )}
          </>
        )}
        <ProviderConcurrencyField provider="ollama" label="Ollama" />
      </div>
      {removing && (
        <ConfirmDialog
          title="移除 Ollama 配置"
          message="移除后使用 Ollama 的审核与脱敏将无法执行；既有任务产物不受影响。"
          requireTyping="Ollama"
          tone="danger"
          onConfirm={() => remove.mutate()}
          onClose={() => setRemoving(false)}
        />
      )}
    </Dialog>
  );
}
